export type ThemeColor = 'red' | 'blue' | 'purple' | 'green' | 'orange' | 'pink';

export type Mood =
  | 'happy'
  | 'sad'
  | 'excited'
  | 'relaxed'
  | 'scared'
  | 'romantic';

export interface WatchlistItem {
  id: number;
  mediaType: 'movie' | 'tv';
  title: string;
  posterPath: string | null;
  backdropPath?: string | null;
  voteAverage?: number;
  releaseDate?: string;
  addedAt: number;
}

export interface WatchProgress {
  mediaId: number;
  mediaType: 'movie' | 'tv';
  title: string;
  posterPath: string | null;
  progress: number; // 0-100
  currentTime: number; // in seconds
  duration: number; // in seconds
  updatedAt: number; // timestamp
  season?: number;
  episode?: number;
}

export interface ExternalIds {
  imdb_id?: string | null;
}

export interface CastMember {
  id: number;
  name: string;
  character: string;
  profile_path: string | null;
}

// Player Events (from Vidking)
export interface PlayerEvent {
  type: 'PLAYER_EVENT';
  data: {
    event: 'timeupdate' | 'play' | 'pause' | 'ended' | 'seeked';
    currentTime: number;
    duration: number;
    progress: number;
    id: string;
    mediaType: 'movie' | 'tv';
    season?: number;
    episode?: number;
    timestamp: number;
  };
}

// TMDB API Types
export interface Movie {
  id: number;
  title: string;
  original_title: string;
  overview: string;
  poster_path: string | null;
  backdrop_path: string | null;
  release_date: string;
  vote_average: number;
  vote_count: number;
  popularity: number;
  genre_ids?: number[];
  genres?: Genre[];
  adult: boolean;
  original_language: string;
  video: boolean;
  runtime?: number;
  tagline?: string;
  status?: string;
  budget?: number;
  revenue?: number;
  videos?: {
    results: Video[];
  };
  recommendations?: MediaResponse<Movie>;
  external_ids?: ExternalIds;
  credits?: {
    cast: CastMember[];
  };
  belongs_to_collection?: {
    id: number;
    name: string;
    poster_path: string | null;
    backdrop_path: string | null;
  };
}

export interface TvShow {
  id: number;
  name: string;
  original_name: string;
  overview: string;
  poster_path: string | null;
  backdrop_path: string | null;
  first_air_date: string;
  last_air_date?: string;
  vote_average: number;
  vote_count: number;
  popularity: number;
  genre_ids?: number[];
  genres?: Genre[];
  origin_country: string[];
  original_language: string;
  number_of_seasons: number;
  number_of_episodes: number;
  status: string;
  tagline?: string;
  type?: string;
  seasons?: Season[];
  videos?: {
    results: Video[];
  };
  recommendations?: MediaResponse<TvShow>;
  external_ids?: ExternalIds;
  credits?: {
    cast: CastMember[];
  };
  next_episode_to_air?: {
    air_date: string;
    episode_number: number;
    name: string;
    season_number: number;
  };
  last_episode_to_air?: {
    air_date: string;
    episode_number: number;
    name: string;
    season_number: number;
  };
}

export interface SearchResult {
  id: number;
  media_type: 'movie' | 'tv' | 'person';
  title?: string;
  name?: string;
  original_title?: string;
  original_name?: string;
  overview?: string;
  poster_path: string | null;
  backdrop_path: string | null;
  release_date?: string;
  first_air_date?: string;
  vote_average?: number;
  popularity: number;
  genre_ids?: number[];
}

export interface MediaResponse<T> {
  page: number;
  results: T[];
  total_pages: number;
  total_results: number;
}

export interface Genre {
  id: number;
  name: string;
}

export type VideoSite = 'YouTube' | 'Vimeo';
export type VideoType = 'Trailer' | 'Teaser' | 'Clip' | 'Featurette' | 'Behind the Scenes' | 'Bloopers' | 'Opening Credits';

export interface Video {
  id: string;
  key: string;
  name: string;
  site: VideoSite | string;
  type: VideoType | string;
  official: boolean;
}

export interface Season {
  id: number;
  name: string;
  overview: string;
  season_number: number;
  episode_count: number;
  air_date: string;
  poster_path: string | null;
  episodes?: Episode[];
}

export interface Episode {
  id: number;
  episode_number: number;
  season_number: number;
  name: string;
  overview: string;
  air_date: string;
  runtime: number;
  still_path: string | null;
  vote_average: number;
  vote_count: number;
}

// Helper functions
export function getMediaTitle(media: Movie | TvShow | SearchResult): string {
  if ('title' in media && media.title) return media.title;
  if ('name' in media && media.name) return media.name;
  return '';
}

export function getMediaDate(media: Movie | TvShow | SearchResult): string {
  if ('release_date' in media && media.release_date) return media.release_date;
  if ('first_air_date' in media && media.first_air_date) return media.first_air_date;
  return '';
}

// Mood to Genre mapping
export const MOOD_GENRES: Record<Mood, number[]> = {
  happy: [35, 10751, 10402], // Comedy, Family, Music
  sad: [18, 10749], // Drama, Romance
  excited: [28, 12, 878], // Action, Adventure, Sci-Fi
  relaxed: [99, 36], // Documentary, History
  scared: [27, 53], // Horror, Thriller
  romantic: [10749, 35], // Romance, Comedy
};

// NBA / Sports Types
export interface NBATeam {
  id: number;
  abbreviation: string;
  city: string;
  conference: string;
  division: string;
  full_name: string;
  name: string;
}

export interface NBAGame {
  id: number;
  date: string;
  home_team: NBATeam;
  visitor_team: NBATeam;
  home_team_score: number;
  visitor_team_score: number;
  status: 'scheduled' | 'in_progress' | 'final';
  time?: string; // Game time in EST
  period?: number;
  postseason: boolean;
  season: number;
}

export interface StreamLink {
  id: string;
  url: string;
  source: string;
  quality: 'HD' | 'SD' | '4K';
  status: 'healthy' | 'buffering' | 'dead' | 'wrong_content';
  lastChecked: number;
}

export interface SportsEvent {
  id: string;
  league: 'NBA' | 'NFL' | 'MLB' | 'NHL' | 'Soccer';
  homeTeam: string;
  awayTeam: string;
  startTime: Date;
  status: 'upcoming' | 'live' | 'final';
  score?: { home: number; away: number };
  streams: StreamLink[];
  isFavoriteTeam: boolean;
}

// ============================================
// SMART WATCHLIST TYPES
// ============================================

export type WatchStatus =
  | 'want_to_watch'      // Haven't started
  | 'watching'           // Currently watching
  | 'caught_up'          // Up to date, waiting for more
  | 'waiting_new_season' // Specifically waiting for next season
  | 'waiting_new_movie'  // Waiting for sequel
  | 'completed'          // Finished
  | 'dropped';           // Stopped watching

export type WatchPriority = 'normal' | 'high' | 'goat';  // goat = One Piece level

export interface SmartWatchProgress {
  currentSeason?: number;
  currentEpisode?: number;
  totalSeasons?: number;
  totalEpisodes?: number;
  lastWatched?: string;  // ISO date string
}

export interface ReleaseTracking {
  enabled: boolean;
  nextRelease?: {
    type: 'season' | 'episode' | 'movie';
    date?: string;
    title?: string;
    announced: boolean;
  };
  notifyMe: boolean;
}

export interface RecapInfo {
  needed: boolean;
  lastRecapWatched?: string;
  suggestedVideoIds?: string[];
}

export interface SmartWatchlistItem {
  // Core identification
  id: number;
  tmdbId: number;
  title: string;
  type: 'movie' | 'tv' | 'anime';
  posterPath: string;
  backdropPath?: string;

  // Status and priority
  status: WatchStatus;
  priority: WatchPriority;

  // Progress tracking
  progress?: SmartWatchProgress;

  // Release tracking
  tracking: ReleaseTracking;

  // Recap support
  recap?: RecapInfo;

  // Metadata
  addedAt: number;
  updatedAt?: number;
  platform?: string;  // Netflix, Crunchyroll, etc.
  notes?: string;
}

// Release update from Gemini
export interface ReleaseUpdate {
  title: string;
  hasUpdate: boolean;
  updateType: 'released' | 'announced' | 'renewed' | 'cancelled';
  releaseDate?: string;
  details: string;
}

// YouTube video type
export interface YouTubeVideo {
  id: string;
  title: string;
  thumbnail: string;
  channelTitle: string;
  publishedAt: string;
  description?: string;
}

// Next release info for watchlist items
export interface NextReleaseInfo {
  type: 'episode' | 'season' | 'movie';
  date: string | null;
  title: string;
  announced: boolean;
}
